import {
  Box,
  ChartContainer,
  ChartGrid,
  ChartMarker,
  ChartTooltip,
  Line,
  Stack,
  TimeXAxis,
  Typography,
  YAxis,
} from '@cast/design-system';
import { dashesOrNum, isE2E } from '@cast/utils';

import { HiddenDot } from 'components/chart';
import { useReactRouterStrategy } from 'components/chart/hooks';

import { EventMarker } from '../../../_components/ChartSection/EventMarker';
import { EventTooltipHeader } from '../../../_components/ChartSection/EventTooltipHeader';
import { RIGHTSIZING_QUERY_PARAM_KEYS } from '../../../_hooks/useRightsizingTimeRangeSelection';
import { TimeSeriesMetricWithEvent } from '../../../_types';

const useZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: RIGHTSIZING_QUERY_PARAM_KEYS,
});

type HpaChartsContentProps = {
  podsChartData: TimeSeriesMetricWithEvent[];
  cpuChartData: TimeSeriesMetricWithEvent[];
  podsAnalyzeWithOpsPilot: ReturnType<
    typeof import('@cast/features-organization-shared-opspilot').useAnalyzeWithOpsPilot
  >;
  cpuAnalyzeWithOpsPilot: ReturnType<
    typeof import('@cast/features-organization-shared-opspilot').useAnalyzeWithOpsPilot
  >;
  timeZone: string;
  isLoading: boolean;
};

export const HpaChartsContent = ({
  podsChartData,
  cpuChartData,
  podsAnalyzeWithOpsPilot,
  cpuAnalyzeWithOpsPilot,
  timeZone,
  isLoading,
}: HpaChartsContentProps) => (
  <Stack height="100%">
    <Stack
      flex={1}
      minHeight={0}
      gap={12}
      sx={{
        // Hiding xAxis through recharts api also prevents bottom-most y axis label from rendering
        '& .recharts-xAxis.xAxis': { display: 'none' },
      }}
    >
      <Typography variant="L10M" pt={8}>
        pod count
      </Typography>
      <Box
        flex={1}
        minHeight={0}
        position="relative"
        data-testid="hpa-chart-pods"
      >
        <ChartContainer
          data={podsChartData}
          isLoading={isLoading}
          syncId="workload-metrics"
          chartActionsSx={{ top: 0, right: 0 }}
          zoomProps={{
            xAxisDataKey: 'chartTimestampUnix',
            useZoomState,
          }}
          analyzeWithOpsPilot={podsAnalyzeWithOpsPilot}
        >
          <ChartGrid hideVertical />
          <Line<TimeSeriesMetricWithEvent>
            dataKey="value"
            stroke="indigo.300"
            strokeWidth={1.5}
            tooltipConfig={{ displayName: 'Pod count' }}
            dot={isE2E() ? HiddenDot : false}
          />
          <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
          <YAxis tickCount={4} allowDecimals={false} />
          <ChartTooltip<TimeSeriesMetricWithEvent>
            testId="data-popper"
            header={(props) => (
              <EventTooltipHeader {...props} timezone={timeZone} />
            )}
            formatValue={(value) => dashesOrNum({ value, roundBy: 3 })}
          />
          <ChartMarker<TimeSeriesMetricWithEvent>
            unixDataKey="chartTimestampUnix"
            shape={EventMarker}
            shouldRender={(payload) => !!payload.event}
          />
        </ChartContainer>
      </Box>
    </Stack>

    <Stack flex={1} minHeight={0} gap={12}>
      <Typography variant="L10M" pt={8}>
        total usage across all pods
      </Typography>
      <Box
        flex={1}
        minHeight={0}
        position="relative"
        data-testid="hpa-chart-cpu"
      >
        <ChartContainer
          data={cpuChartData}
          isLoading={isLoading}
          syncId="workload-metrics"
          chartActionsSx={{ top: 0, right: 0 }}
          zoomProps={{
            xAxisDataKey: 'chartTimestampUnix',
            useZoomState,
          }}
          analyzeWithOpsPilot={cpuAnalyzeWithOpsPilot}
        >
          <ChartGrid hideVertical />
          <Line<TimeSeriesMetricWithEvent>
            dataKey="value"
            stroke="green.400"
            strokeWidth={1.5}
            tooltipConfig={{ displayName: 'Total usage', unit: 'CPU' }}
            dot={isE2E() ? HiddenDot : false}
          />
          <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
          <YAxis tickCount={4} unitFormatter={(tick) => `${tick} CPU`} />
          <ChartTooltip<TimeSeriesMetricWithEvent>
            testId="data-popper"
            header={(props) => (
              <EventTooltipHeader {...props} timezone={timeZone} />
            )}
            formatValue={(value) => dashesOrNum({ value, roundBy: 3 })}
          />
          <ChartMarker<TimeSeriesMetricWithEvent>
            unixDataKey="chartTimestampUnix"
            shape={EventMarker}
            shouldRender={(payload) => !!payload.event}
          />
        </ChartContainer>
      </Box>
    </Stack>
  </Stack>
);
